import React, { useState } from 'react';
import { bcGovernmentServices, getServicesByCategory } from '../../data/bcGovernmentServices';
import SimpleLanguageSelector from '../SimpleLanguageSelector';

interface SidebarProps {
  isOpen: boolean;
  primaryColor: string;
  secondaryColor: string;
  currentPage?: string;
  onNavigate?: (page: string) => void;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  primaryColor,
  secondaryColor,
  currentPage = 'home',
  onNavigate,
  activeSection = 'overview',
  onSectionChange
}) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const servicesByCategory = getServicesByCategory();
  const categories = Array.from(servicesByCategory.keys());

  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      'Identity & Documents': '🆔',
      'Family & Children': '👨‍👩‍👧‍👦',
      'Financial & Tax': '💰',
      'Housing & Transportation': '🏠',
      'Employment & Education': '🎓',
      'Health & Wellness': '🏥',
      'Legal & Social': '⚖️',
      'Emergency & Essential': '🚨',
    };
    return icons[category] || '📋';
  };

  const renderSidebarContent = () => {
    if (currentPage === 'services') {
      return (
        <>
          <div className="px-6 pb-4 border-b border-white/20 mb-4">
            <h2 className="text-white text-xl font-bold mb-1">Service Categories</h2>
            <p className="text-white/80 text-sm">{bcGovernmentServices.length} services available</p>
          </div>

          <nav className="flex flex-col gap-2">
            {categories.map((category) => {
              const services = servicesByCategory.get(category) || [];
              const isExpanded = expandedCategory === category;
              return (
                <div key={category}>
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-6 py-3 border-none text-white cursor-pointer flex items-center gap-2 text-[0.95rem] transition-colors text-left hover:bg-white/10"
                    style={{ backgroundColor: isExpanded ? secondaryColor : 'transparent' }}
                  >
                    <span className="text-xl">{getCategoryIcon(category)}</span>
                    <span className="flex-1 font-medium">{category}</span>
                    <span className="text-sm opacity-80">({services.length})</span>
                    <span className="text-xs">{isExpanded ? '▼' : '▶'}</span>
                  </button>

                  {isExpanded && (
                    <div className="bg-black/20 py-2">
                      {services.map((service) => (
                        <a
                          key={service.id}
                          href={service.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block py-3 pl-12 pr-6 text-white no-underline hover:bg-white/10 border-l-[3px] border-transparent hover:border-white/40 transition-colors"
                        >
                          <div className="text-sm font-medium mb-1">{service.name}</div>
                          <div className="text-xs opacity-80 leading-snug">
                            {service.description.substring(0, 60)}...
                          </div>
                          {service.priority === 'high' && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-300 text-yellow-900 text-xs font-bold rounded-full">
                              High Priority
                            </span>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="mt-8 px-6 pt-4 border-t border-white/20">
            <p className="text-white text-sm mb-3">Need help? Call <strong>BC 211</strong></p>
            <a
              href="tel:211"
              className="block w-full py-3 text-center text-white font-bold rounded no-underline hover:opacity-90 transition-opacity"
              style={{ backgroundColor: secondaryColor }}
            >
              📞 Dial 211
            </a>
          </div>
        </>
      );
    }

    if (currentPage === 'dashboard') {
      return (
        <>
          <div className="px-6 pb-4 border-b border-white/20 mb-4">
            <h2 className="text-white text-xl font-bold mb-1">Canada Info</h2>
            <p className="text-white/80 text-sm">Quick access to information</p>
          </div>

          <nav className="flex flex-col gap-2">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-white text-xs font-bold mb-3 uppercase tracking-wider">📊 Dashboard Sections</h3>
              {[
                { id: 'overview', icon: 'ℹ️', label: 'Overview' },
                { id: 'weather', icon: '🌤️', label: 'Weather' },
                { id: 'population', icon: '👥', label: 'Population' },
                { id: 'economy', icon: '📈', label: 'Economy' },
                { id: 'currency', icon: '💱', label: 'Currency' },
              ].map(({ id, icon, label }) => (
                <button
                  key={id}
                  onClick={() => onSectionChange?.(id)}
                  className="flex items-center gap-3 w-full px-3 py-3 rounded-md mb-2 border-none cursor-pointer text-[0.95rem] font-medium transition-colors hover:bg-white/10"
                  style={{
                    backgroundColor: activeSection === id ? secondaryColor : 'transparent',
                    color: activeSection === id ? '#111827' : 'white',
                  }}
                >
                  <span className="text-xl">{icon}</span>
                  {label}
                </button>
              ))}
            </div>

            <div className="px-6 py-4">
              <h3 className="text-white text-xs font-bold mb-3 uppercase tracking-wider">🔗 External Links</h3>
              {[
                { href: 'https://www.canada.ca', icon: '🍁', label: 'Canada.ca' },
                { href: 'https://www2.gov.bc.ca', icon: '🏛️', label: 'BC Government' },
                { href: 'https://www.welcomebc.ca', icon: '👋', label: 'WelcomeBC' },
              ].map(({ href, icon, label }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-2 py-2 text-white no-underline rounded mb-2 hover:bg-white/10 transition-colors text-sm"
                >
                  <span className="text-xl">{icon}</span>
                  {label}
                </a>
              ))}
            </div>
          </nav>
        </>
      );
    }

    // Home page
    return (
      <>
        <div className="px-6 pb-4 border-b border-white/20 mb-4">
          <h2 className="text-white text-xl font-bold mb-1">Navigation</h2>
          <p className="text-white/80 text-sm">Find what you need</p>
        </div>

        <nav className="flex flex-col gap-2">
          {[
            { id: 'home', icon: '🏠', title: 'Home', desc: 'Find your services' },
            { id: 'dashboard', icon: '📊', title: 'Dashboard', desc: 'Canada information' },
            { id: 'services', icon: '🔧', title: 'Services', desc: 'Browse all BC services' },
          ].map(({ id, icon, title, desc }) => (
            <button
              key={id}
              onClick={() => onNavigate?.(id)}
              className="w-full px-6 py-4 border-none text-white cursor-pointer flex items-center gap-4 transition-colors text-left hover:bg-white/10"
              style={{ backgroundColor: currentPage === id ? secondaryColor : 'transparent' }}
            >
              <span className="text-3xl">{icon}</span>
              <div>
                <div className="font-bold text-base">{title}</div>
                <div className="text-xs opacity-80">{desc}</div>
              </div>
            </button>
          ))}
        </nav>

        <div className="mt-8 px-6 pt-4 border-t border-white/20">
          <p className="text-white text-sm mb-3">Need help? Call <strong>BC 211</strong></p>
          <a
            href="tel:211"
            className="block w-full py-3 text-center text-white font-bold rounded no-underline hover:opacity-90 transition-opacity"
            style={{ backgroundColor: secondaryColor }}
          >
            📞 Dial 211
          </a>
        </div>

        <div className="mt-4 px-6 pt-4 border-t border-white/15 text-center">
          <p className="text-white/60 text-xs mb-0.5">🛠️ Built by</p>
          <p className="text-white text-sm font-bold tracking-wide mb-1">NATION CODE CANADA</p>
          <a href="mailto:nationcode@proton.me" className="text-white/75 text-xs underline">
            nationcode@proton.me
          </a>
        </div>
      </>
    );
  };

  return (
    <aside
      className="fixed left-0 top-[60px] w-[280px] h-[calc(100vh-60px)] overflow-y-auto transition-transform duration-300 shadow-md z-[90] py-6"
      style={{
        backgroundColor: primaryColor,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
      }}
    >
      {renderSidebarContent()}


    </aside>
  );
};

export default Sidebar;
